import type {
  LoginTokensResponse,
  PasswordResetVerificationResponse,
} from '@/types/auth';
import type { Beneficiary } from '@/types/beneficiary';
import type { RoleSegment, Segment } from '@/types/segment';
import type { Store } from '@/types/store';
import type { User } from '@/types/user';

type ContractValidationContext = {
  operation: string;
  endpoint?: string;
  itemIndex?: number;
};

const formatContext = (context: ContractValidationContext) => {
  const endpointPart = context.endpoint ? ` (${context.endpoint})` : '';
  const indexPart =
    typeof context.itemIndex === 'number' ? ` [item ${context.itemIndex}]` : '';
  return `${context.operation}${endpointPart}${indexPart}`;
};

export class ApiContractError extends Error {
  constructor(message: string, context: ContractValidationContext) {
    super(
      `Contract validation failed for ${formatContext(context)}: ${message}`,
    );
    this.name = 'ApiContractError';
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const ensureRecord = (value: unknown, context: ContractValidationContext) => {
  if (!isRecord(value)) {
    throw new ApiContractError('Expected an object response', context);
  }
  return value;
};

const ensureArray = (value: unknown, context: ContractValidationContext) => {
  if (!Array.isArray(value)) {
    throw new ApiContractError('Expected an array response', context);
  }
  return value;
};

const ensureStringField = (
  value: Record<string, unknown>,
  key: string,
  context: ContractValidationContext,
) => {
  if (typeof value[key] !== 'string' || value[key] === '') {
    throw new ApiContractError(
      `Expected non-empty string field "${key}"`,
      context,
    );
  }
};

const ensureNumberOrStringField = (
  value: Record<string, unknown>,
  key: string,
  context: ContractValidationContext,
) => {
  const fieldValue = value[key];
  if (
    fieldValue === undefined ||
    fieldValue === null ||
    (typeof fieldValue !== 'string' && typeof fieldValue !== 'number')
  ) {
    throw new ApiContractError(
      `Expected number|string field "${key}"`,
      context,
    );
  }
};

const ensureRecordField = (
  value: Record<string, unknown>,
  key: string,
  context: ContractValidationContext,
) => {
  const fieldValue = value[key];
  if (!isRecord(fieldValue)) {
    throw new ApiContractError(`Expected object field "${key}"`, context);
  }
  return fieldValue;
};

const ensureArrayField = (
  value: Record<string, unknown>,
  key: string,
  context: ContractValidationContext,
) => {
  const fieldValue = value[key];
  if (!Array.isArray(fieldValue)) {
    throw new ApiContractError(`Expected array field "${key}"`, context);
  }
  return fieldValue;
};

const ensureBooleanField = (
  value: Record<string, unknown>,
  key: string,
  context: ContractValidationContext,
) => {
  if (typeof value[key] !== 'boolean') {
    throw new ApiContractError(`Expected boolean field "${key}"`, context);
  }
};

const ensureNumberField = (
  value: Record<string, unknown>,
  key: string,
  context: ContractValidationContext,
) => {
  if (typeof value[key] !== 'number' || Number.isNaN(value[key])) {
    throw new ApiContractError(`Expected number field "${key}"`, context);
  }
};

export type LoginTokensResponseContract = unknown;

export const validateLoginTokensResponse = (
  payload: LoginTokensResponseContract,
  context: ContractValidationContext,
): LoginTokensResponse => {
  const record = ensureRecord(payload, context);
  ensureStringField(record, 'token', context);
  ensureStringField(record, 'refresh_token', context);
  ensureStringField(record, 'expires_at', context);
  ensureStringField(record, 'token_type', context);
  return payload as LoginTokensResponse;
};

export type PasswordResetVerificationContract = unknown;

export const validatePasswordResetVerificationResponse = (
  payload: PasswordResetVerificationContract,
  context: ContractValidationContext,
): PasswordResetVerificationResponse => {
  const record = ensureRecord(payload, context);
  ensureStringField(record, 'reset_token', context);
  ensureStringField(record, 'expires_at', context);
  return payload as PasswordResetVerificationResponse;
};

export type UserContract = unknown;

export const validateUserResponse = (
  payload: UserContract,
  context: ContractValidationContext,
): User => {
  const record = ensureRecord(payload, context);
  ensureNumberOrStringField(record, 'id', context);
  ensureStringField(record, 'email', context);
  ensureStringField(record, 'status', context);
  ensureStringField(record, 'role', context);
  return payload as User;
};

export type StoreContract = unknown;

export const validateStoreResponse = (
  payload: StoreContract,
  context: ContractValidationContext,
): Store => {
  const record = ensureRecord(payload, context);
  ensureNumberOrStringField(record, 'id', context);
  ensureStringField(record, 'external_id', context);
  ensureStringField(record, 'name', context);
  ensureStringField(record, 'type', context);
  ensureStringField(record, 'status', context);
  return payload as Store;
};

export type SegmentContract = unknown;

export const validateSegmentResponse = (
  payload: SegmentContract,
  context: ContractValidationContext,
): Segment => {
  const record = ensureRecord(payload, context);
  if (record.id !== undefined && record.id !== null) {
    ensureNumberOrStringField(record, 'id', context);
  }
  ensureStringField(record, 'name', context);
  ensureStringField(record, 'label', context);
  ensureStringField(record, 'status', context);
  ensureNumberField(record, 'discount_percentage_cap', context);
  ensureBooleanField(record, 'monthly_recharge_enabled', context);
  return payload as Segment;
};

export type BeneficiaryContract = unknown;

export const validateBeneficiaryResponse = (
  payload: BeneficiaryContract,
  context: ContractValidationContext,
): Beneficiary => {
  const record = ensureRecord(payload, context);
  ensureNumberOrStringField(record, 'id', context);
  ensureStringField(record, 'email', context);
  ensureStringField(record, 'status', context);

  const profile = ensureRecordField(record, 'profile', context);
  ensureStringField(profile, 'first_name', context);
  ensureStringField(profile, 'last_name', context);

  const segment = ensureRecordField(record, 'segment', context);
  ensureStringField(segment, 'name', context);
  ensureStringField(segment, 'label', context);

  const wallet = ensureRecordField(record, 'wallet', context);
  if (
    wallet.allocated_balance === undefined ||
    wallet.allocated_balance === null
  ) {
    throw new ApiContractError(
      'Expected non-null field "wallet.allocated_balance"',
      context,
    );
  }

  return payload as Beneficiary;
};

export type RoleSegmentsContract = unknown;

export const validateRoleSegmentsResponse = (
  payload: RoleSegmentsContract,
  context: ContractValidationContext,
): RoleSegment[] => {
  const list = ensureArray(payload, context);

  list.forEach((item, index) => {
    const itemContext = {
      ...context,
      itemIndex: index,
    };
    const roleSegment = ensureRecord(item, itemContext);
    ensureStringField(roleSegment, 'role', itemContext);

    const segments = ensureArrayField(roleSegment, 'segments', itemContext);
    segments.forEach((segmentItem, segmentIndex) => {
      const segmentContext = {
        ...itemContext,
        itemIndex: segmentIndex,
      };
      const segment = ensureRecord(segmentItem, segmentContext);
      ensureStringField(segment, 'name', segmentContext);
      ensureStringField(segment, 'label', segmentContext);
    });
  });

  return payload as RoleSegment[];
};
